import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Home from "@/pages/Home";
import Pricing from "@/pages/Pricing";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import CreateListing from "@/pages/CreateListing";
import ListingDetail from "@/pages/ListingDetail";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import PublicListing from "@/pages/PublicListing";
import AuthCallback from "@/pages/AuthCallback";
import MarketData from "@/pages/MarketData";
import Contacts from "@/pages/Contacts";
import Support from "@/pages/Support";
import MortgageCalculator from "@/pages/MortgageCalculator";
import AffordabilityCalculator from "@/pages/AffordabilityCalculator";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";

// Components
import SupportChat from "@/components/SupportChat";

// Auth context
import { AuthProvider, useAuth } from "@/context/AuthContext";
// Language context
import { LanguageProvider } from "@/context/LanguageContext";
// Analytics hook
import { useAnalytics } from "@/hooks/useAnalytics";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Router component that handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Initialize analytics tracking
  useAnalytics();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  // Check both location.hash and window.location.hash for robustness
  const hash = location.hash || window.location.hash || '';
  
  if (hash.includes('session_id=')) {
    console.log('OAuth callback detected, rendering AuthCallback');
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/support" element={<Support />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/share/:shareToken" element={<PublicListing />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/listings/new" element={
        <ProtectedRoute><CreateListing /></ProtectedRoute>
      } />
      <Route path="/listings/:id" element={
        <ProtectedRoute><ListingDetail /></ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute><Account /></ProtectedRoute>
      } />
      <Route path="/market-data" element={
        <ProtectedRoute><MarketData /></ProtectedRoute>
      } />
      <Route path="/contacts" element={
        <ProtectedRoute><Contacts /></ProtectedRoute>
      } />
      <Route path="/mortgage-calculator" element={
        <ProtectedRoute><MortgageCalculator /></ProtectedRoute>
      } />
      <Route path="/affordability-calculator" element={
        <ProtectedRoute><AffordabilityCalculator /></ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute><Admin /></AdminRoute>
      } />
    </Routes>
  );
};

function App() {
  // Check for OAuth callback at the very top level, before any providers
  // This ensures we don't have race conditions with auth state
  const [isOAuthCallback] = useState(() => {
    const hash = window.location.hash || '';
    return hash.includes('session_id=');
  });

  return (
    <LanguageProvider>
      <AuthProvider skipInitialCheck={isOAuthCallback}>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
          {/* Floating Support Chat Widget - appears on all pages except /support */}
          <FloatingChatWrapper />
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

// Wrapper to conditionally show chat widget (hide on /support page to avoid duplicate)
const FloatingChatWrapper = () => {
  const location = useLocation();
  // Don't show floating widget on the support page (it has its own full chat)
  if (location.pathname === '/support') return null;
  return <SupportChat />;
};

export default App;
